/**
 * FRONT-1809: moved out of `~/hooks/mutations/use-okf-bundle.ts` (where it
 * was a private, unexported helper) so the Sketches download flow
 * (`~/features/sketches/sketch-download.ts`) can reuse it too, rather than
 * growing a second copy. FRONT-2024: the shared Apps download flow
 * (`~/features/apps/app-download.ts`) reuses it the same way.
 * `use-graph-export-import.ts` still carries its own pre-existing copy —
 * untouched here, out of this ticket's scope.
 */

/**
 * Strip anything that could make a suggested name escape its directory. A
 * `Content-Disposition` filename is server-supplied text, so it never gets
 * to carry a path — the browser sanitizes the `download` attribute too,
 * but a name is easier to reason about if it can only ever be one. `null`
 * when nothing usable is left, so the caller can fall back to a locally-
 * built name — including when the cleaned name is nothing but dots (`.`,
 * `..`, `...`), which is meaningless as a filename on its own.
 *
 * Exported (FRONT-1809 adversarial review, finding 10) so
 * `~/features/sketches/sketch-download.ts` can run its own client-built
 * fallback name through the SAME sanitizer every server-supplied name
 * already goes through here, rather than saving a raw, unsanitized title.
 *
 * FRONT-1809 adversarial review (finding 13): this used to also strip
 * every LEADING dot (`^\.+`), which silently renamed a legitimate name
 * like `.env explained.html` to `env explained.html` — a real content
 * title, not a traversal attempt. Once path separators are replaced above,
 * a leading dot is not a traversal risk (there is no `..` left that could
 * still resolve to a parent directory), so it is no longer stripped; only
 * a name that is NOTHING BUT dots is rejected outright.
 */
export function bareName(value: string): string | null {
  const cleaned = value.trim().replace(/[\\/]/g, "_");
  if (!cleaned || /^\.+$/.test(cleaned)) return null;
  return cleaned;
}

/**
 * `attachment; filename="Q3 report.html"; filename*=UTF-8''Q3%20report.html`
 * → the name the server suggested. `null` when the header is absent or
 * unparseable, so the caller can fall back to a locally-built name.
 *
 * **`filename*` wins when both are present** (RFC 6266 §4.3). This is not a
 * detail: the two halves carry DIFFERENT text by design — the plain
 * `filename=` half must stay ASCII, so a real title like
 * `Podcast Launch – Konzept & Ablauf` reaches it with the en dash stripped,
 * while `filename*` carries it intact. Taking whichever appears first in
 * the header (the previous behaviour) therefore always picked the degraded
 * name, and did so silently — the download still worked, it was just named
 * wrong, which is exactly the kind of defect nobody files.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  // RFC 5987 extended parameter: `filename*=<charset>'<language>'<pct-encoded>`.
  const extended = /filename\*=\s*(?:UTF-8|ISO-8859-1)?'[^']*'([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      const decoded = bareName(decodeURIComponent(extended[1].trim()));
      if (decoded) return decoded;
    } catch {
      // Malformed percent-encoding — fall through to the plain parameter
      // rather than failing the whole download over a name.
    }
  }

  // Plain parameter. `filename\*?=` would also match the extended one, so
  // require the `=` to follow the word directly.
  const plain = /filename=(?:"([^"]*)"|([^;]+))/i.exec(header);
  const raw = plain?.[1] ?? plain?.[2];
  return raw ? bareName(raw) : null;
}
